import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, Spinner } from 'reactstrap';
import styled from 'styled-components';

// Import SVG assets
import arrowLeftIcon from '../../assets/img/icons/arrow_left_gallery.svg';
import arrowRightIcon from '../../assets/img/icons/arrow_right_gallery.svg';
import audioIcon from '../../assets/img/icons/audio_gallery.svg';

// Styled Components
const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(34, 102, 239, 0.9);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1001;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover:not(:disabled) {
    background-color: rgba(34, 102, 239, 1);
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &:focus {
    outline: 2px solid rgba(34, 102, 239, 0.5);
    outline-offset: 2px;
  }
`;

const PrevButton = styled(NavigationButton)`
  left: 16px;
`;

const NextButton = styled(NavigationButton)`
  right: 16px;
`;

const ArrowIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const Counter = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 10px 20px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  z-index: 1001;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  user-select: none;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 24px;
  line-height: 1;
  z-index: 1002;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }

  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }
`;

const MediaContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  max-height: 80vh;
  width: 100%;
`;

const StyledImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
`;

const AudioContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  min-width: 400px;
`;

const AudioIcon = styled.div`
  width: 80px;
  height: 80px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;

  img {
    width: 40px;
    height: 40px;
  }
`;

const AudioPlayer = styled.audio`
  width: 100%;
  max-width: 350px;
  outline: none;

  &::-webkit-media-controls-panel {
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 8px;
  }
`;

const AudioLabel = styled.div`
  color: white;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
`;

/**
 * EvidenceGalleryModal - Reusable modal for browsing images and audio files
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {Array} items - Array of evidence items: [{ type: "image"|"audio", src: "url", label?: "optional" }]
 * @param {number} startIndex - Initial item index to display
 * @param {function} onClose - Callback when modal closes
 */
const EvidenceGalleryModal = ({ isOpen, items = [], startIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Reset index and loading state when modal opens or items change
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(startIndex);
      setImageLoaded(false);
    }
  }, [isOpen, startIndex]);

  // Reset image loaded state when changing items
  useEffect(() => {
    setImageLoaded(false);
  }, [currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowRight':
          handleNext();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, items.length]);

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  if (!isOpen || items.length === 0) return null;

  const currentItem = items[currentIndex];
  const hasMultipleItems = items.length > 1;
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < items.length - 1;

  return (
    <>
      {!imageLoaded && currentItem?.type === 'image' && (
        <LoadingSpinner>
          <Spinner color="primary" />
        </LoadingSpinner>
      )}
      
      <Modal
        isOpen={isOpen}
        toggle={onClose}
        style={{
          marginTop: '5vh',
          width: '80vw',
          maxWidth: currentItem?.type === 'audio' ? '600px' : '900px',
          visibility: currentItem?.type === 'audio' || imageLoaded ? 'visible' : 'hidden',
        }}
        backdrop="static"
      >
        <ModalBody style={{ padding: '20px', position: 'relative', backgroundColor: '#f5f5f5' }}>
          <CloseButton onClick={onClose} aria-label="Close">
            ×
          </CloseButton>

          {/* Previous Button */}
          {hasMultipleItems && (
            <PrevButton
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              aria-label="Previous"
              title="Previous (←)"
            >
              <ArrowIcon src={arrowLeftIcon} alt="Previous" />
            </PrevButton>
          )}

          {/* Media Display */}
          <MediaContainer>
            {currentItem?.type === 'image' && (
              <StyledImage
                src={currentItem.src}
                alt={currentItem.label || `Evidence ${currentIndex + 1}`}
                onLoad={handleImageLoad}
                style={{ visibility: imageLoaded ? 'visible' : 'hidden' }}
              />
            )}

            {currentItem?.type === 'audio' && (
              <AudioContainer>
                <AudioIcon>
                  <img src={audioIcon} alt="Audio" />
                </AudioIcon>
                <AudioLabel>{currentItem.label || 'Audio Evidence'}</AudioLabel>
                <AudioPlayer controls autoPlay>
                  <source src={currentItem.src} type="audio/mpeg" />
                  <source src={currentItem.src} type="audio/wav" />
                  <source src={currentItem.src} type="audio/ogg" />
                  Your browser does not support the audio element.
                </AudioPlayer>
              </AudioContainer>
            )}
          </MediaContainer>

          {/* Next Button */}
          {hasMultipleItems && (
            <NextButton
              onClick={handleNext}
              disabled={!canGoNext}
              aria-label="Next"
              title="Next (→)"
            >
              <ArrowIcon src={arrowRightIcon} alt="Next" />
            </NextButton>
          )}

          {/* Counter */}
          {hasMultipleItems && (
            <Counter>
              {currentIndex + 1} / {items.length}
            </Counter>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default EvidenceGalleryModal;
